// Jurisdiction-specific overlays for the landing page. Each entry overrides a
// handful of fields on the base LandingPage so Marianne can present itself with
// the right legal references, examples, and tone for each region.

export const JURISDICTIONS = {
  in: {
    slug: "in",
    name: "India",
    flag: "🇮🇳",
    greeting: "भारत · Bhārat",
    hero_badge: "Legal Operating System · India",
    hero_tag: "IT Act, 2000 · DPDP Act, 2023 · CPA, 2019",
    tagline: "The complete workspace for Indian legal work — notices, agreements, and cases, done right.",
    examples: {
      agreement: "SaaS Terms, Employment Letter, NDA",
      signature: "IT Act, 2000 — consent-based electronic signatures",
      case: "Consumer Commission, Cheque Bounce (S.138 NI Act), Labour Court",
      evidence: "Section 65B certification support",
    },
    feature_notes: {
      "01": "Includes India-specific templates aligned with the Indian Contract Act, 1872 and the Industrial Employment (Standing Orders) Act, 1946.",
      "02": "Compliant with Section 3A of the IT Act, 2000 — electronic signatures hold the same evidentiary value as handwritten signatures when consent and intent are recorded.",
      "03": "Pre-loaded with case starters for Consumer Complaints (CPA 2019), Cheque Bounce (NI Act, Section 138), Wrongful Termination, and Landlord disputes.",
    },
    audiences: [
      { title: "Consumers", body: "File complaints under the Consumer Protection Act, 2019 with the correct Commission tier (District / State / National)." },
      { title: "Entrepreneurs", body: "MSME agreements, GST-compliant invoicing templates, and partnership deeds under the Indian Partnership Act, 1932." },
      { title: "Businesses", body: "DPDP Act 2023 readiness, employment letters under the Model Standing Orders, vendor and service agreements." },
      { title: "Advocates", body: "Case workspaces mapped to Indian court hierarchies with Section 65B evidence certification support." },
      { title: "Organisations", body: "Compliance workflows for Companies Act 2013, SEBI, and sector regulators (RBI, IRDAI, TRAI)." },
    ],
    ecosystem_note: "Marianne respects the layered jurisdiction of India — Union, State and local — and pre-maps forums for consumer, labour, revenue, criminal and civil matters.",
    pricing_currency: "INR",
    disclaimer: "Marianne is not a law firm and does not provide legal representation. For matters requiring an Advocate enrolled with a Bar Council of India State Bar, please consult one. Template availability and legal requirements vary by state.",
  },
  uk: {
    slug: "uk",
    name: "United Kingdom",
    flag: "🇬🇧",
    greeting: "United Kingdom",
    hero_badge: "Legal Operating System · United Kingdom",
    hero_tag: "UK eIDAS · Consumer Rights Act · UK GDPR",
    tagline: "The modern workspace for UK legal work — contracts, deeds, disputes, done beautifully.",
    examples: {
      agreement: "Shareholders' Agreement, SaaS Terms, Settlement Agreement",
      signature: "Qualified / Advanced electronic signatures under UK eIDAS",
      case: "Small Claims (County Court), Employment Tribunal, Housing Disputes",
      evidence: "Chain-of-custody logs for CPR disclosure",
    },
    feature_notes: {
      "01": "UK-aligned templates covering the Consumer Rights Act 2015, the Companies Act 2006 and the Employment Rights Act 1996.",
      "02": "eIDAS-aligned electronic signatures (UK retained Regulation 910/2014). Qualified-signature upgrade path for execution-as-deeds.",
      "03": "Case starters for Small Claims Online (Money Claims), Employment Tribunal (ET1), and Section 21 / Section 8 housing matters.",
    },
    audiences: [
      { title: "Consumers", body: "Section 75 claims, Consumer Rights Act 2015 refunds, and small claims under the Civil Procedure Rules." },
      { title: "Entrepreneurs", body: "Companies House filings, shareholders' agreements and employment contracts under English law." },
      { title: "Businesses", body: "UK GDPR / Data Protection Act 2018, DSAR workflows, employment settlement agreements." },
      { title: "Solicitors", body: "Matter management mapped to the SRA Code of Conduct and Civil Procedure Rules disclosure obligations." },
      { title: "Organisations", body: "Regulatory workflows for the FCA, ICO, HSE and other UK regulators." },
    ],
    ecosystem_note: "Marianne is designed with the courts of England and Wales, Scotland, and Northern Ireland in mind — including the Online Money Claims and Employment Tribunal portals.",
    pricing_currency: "GBP",
    disclaimer: "Marianne is not a solicitors' firm and does not provide reserved legal activities. For regulated advice, consult a solicitor regulated by the SRA (or the appropriate regulator). Template availability and legal requirements vary across the UK jurisdictions.",
  },
  us: {
    slug: "us",
    name: "United States",
    flag: "🇺🇸",
    greeting: "United States",
    hero_badge: "Legal Operating System · United States",
    hero_tag: "ESIGN Act · UETA · HIPAA · CCPA",
    tagline: "The all-in-one legal workspace for US agreements, signatures, and cases — jurisdiction-aware across all 50 states.",
    examples: {
      agreement: "Mutual NDA, Master Services Agreement, Operating Agreement (LLC)",
      signature: "ESIGN Act (15 U.S.C. §7001) and state UETA — e-signatures legally equivalent to wet-ink",
      case: "Small Claims, Demand Letters, Cease-and-Desist, DMCA notices",
      evidence: "Federal Rules of Evidence authentication support",
    },
    feature_notes: {
      "01": "US-aligned templates covering standard commercial, employment at-will, and consumer contracts — with state-specific overrides where it matters (CA, NY, TX, WA, DE).",
      "02": "ESIGN Act and state UETA compliant — audit trail, consent capture, SHA-256 integrity. Not for documents expressly excluded by ESIGN §7003 (e.g. wills, family-law adoptions).",
      "03": "Case starters for Demand Letter, DMCA Takedown, Cease & Desist, Small Claims (per-state limits), and CCPA / HIPAA data-subject requests.",
    },
    audiences: [
      { title: "Consumers", body: "Small claims demand letters, FDCPA responses, Lemon Law workflows, and CCPA erasure requests." },
      { title: "Founders", body: "LLC Operating Agreements, SAFEs, convertible notes, employee offer letters, and advisor agreements." },
      { title: "Businesses", body: "MSAs, SOWs, DPAs, HIPAA Business Associate Agreements, employee handbooks." },
      { title: "Attorneys", body: "Matter management with per-state statute-of-limitations reminders and FRE-aware evidence organisation." },
      { title: "Organisations", body: "SOC 2 / HIPAA / CCPA document workflows and audit-ready records." },
    ],
    ecosystem_note: "Marianne is designed to work across the dual federal / state system — case workspaces are jurisdiction-tagged so filings, deadlines and templates adapt to the court you're actually in.",
    pricing_currency: "USD",
    disclaimer: "Marianne is not a law firm and does not provide legal advice; unauthorized-practice-of-law restrictions apply in every state. For legal advice, consult a licensed attorney in your state. Template availability and legal requirements vary by state.",
  },
  eu: {
    slug: "eu",
    name: "European Union",
    flag: "🇪🇺",
    greeting: "Union Européenne",
    hero_badge: "Legal Operating System · European Union",
    hero_tag: "eIDAS 910/2014 · GDPR · Rome I / II",
    tagline: "The pan-European legal workspace — contracts, signatures, and cases that respect every Member State.",
    examples: {
      agreement: "Distribution Agreements, SaaS DPAs (Art. 28 GDPR), employment contracts",
      signature: "eIDAS 910/2014 — Simple, Advanced and Qualified Electronic Signatures",
      case: "Small Claims (Reg. 861/2007), payment orders (Reg. 1896/2006), DPA complaints",
      evidence: "GDPR-aligned data minimisation and processing records (Art. 30)",
    },
    feature_notes: {
      "01": "EU-wide templates aligned with Rome I / Rome II conflict-of-laws rules and the Unfair Terms Directive (93/13/EEC).",
      "02": "eIDAS-compliant signatures: Simple (SES), Advanced (AdES) and support flows for Qualified (QES) via accredited Trust Service Providers.",
      "03": "Case starters for GDPR DSARs and erasure (Art. 15, 17), European Small Claims Procedure, and payment orders.",
    },
    audiences: [
      { title: "Consumers", body: "Cross-border consumer rights, European Small Claims Procedure, GDPR data-subject requests to any EU controller." },
      { title: "Entrepreneurs", body: "SaaS DPAs, cross-border service contracts, Rome I-aware governing-law and jurisdiction clauses." },
      { title: "Businesses", body: "GDPR Art. 30 records of processing, DPIAs, Standard Contractual Clauses for international transfers." },
      { title: "Lawyers", body: "Matter management across civil-law and common-law Member States with the right procedural reminders." },
      { title: "Organisations", body: "Workflows for the EDPB, national DPAs, European Court of Justice referrals, and Member-State regulators." },
    ],
    ecosystem_note: "Marianne respects the European legal order — EU law, Member-State law, and the European Convention on Human Rights — and surfaces the right rules by context.",
    pricing_currency: "EUR",
    disclaimer: "Marianne is not a law firm and does not provide legal advice. Local legal-practice regulations (national Bars) apply. Template availability and legal requirements vary across Member States.",
  },
};

export const JURISDICTION_LIST = Object.values(JURISDICTIONS);
